import { useCallback, useEffect, useState } from "react";
import { Button, Form, Typography, message } from "antd";
import { SafetyOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router";
import "./index.css";
import { ForgotPasswordModal } from "./partials/ForgotPasswordModal";
import { LoginHero } from "./partials/LoginHero";
import { LoginForm } from "./partials/LoginForm";
import { getRememberedUserId, applyRememberMe, buildLoginSession, persistLoginSession } from "./login.utils";
import { clearSession, hasValidSession, takeAuthNotice } from "../../utils/session";
import {
  useAllSecurityQuestionsMutation,
  useChangePasswordMutation,
  useLoginMutation,
  useResetPasswordMutation,
  useSecurityQuestionMutation,
} from "./login.hooks";

const { Text } = Typography;



export default function Login() {
  const [form] = Form.useForm();
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotForm] = Form.useForm();
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotStep, setForgotStep] = useState("uid");
  const [forgotMode, setForgotMode] = useState("forgot");
  const [pendingPostResetLogin, setPendingPostResetLogin] = useState(null);
  const [adminSecurityQuestions, setAdminSecurityQuestions] = useState([]);

  const loginMutation = useLoginMutation();
  const securityQuestionMutation = useSecurityQuestionMutation();
  const allSecurityQuestionsMutation = useAllSecurityQuestionsMutation();
  const resetPasswordMutation = useResetPasswordMutation();
  const changePasswordMutation = useChangePasswordMutation();

  useEffect(() => {
    document.title = "Sign In | Watch Tower";
  }, []);

  // Shows a one-time notice, for example after sign-out or an expired session.
  useEffect(() => {
    const notice = takeAuthNotice();
    if (notice?.text) {
      message[notice.type === "warning" ? "warning" : "info"](notice.text);
    }
  }, []);

  // Signed-in users skip the login form, unless a forced password reset is still pending.
  useEffect(() => {
    if (sessionStorage.getItem("token") && !hasValidSession()) {
      clearSession();
    }

    const hasPendingReset = sessionStorage.getItem("pendingPasswordReset") === "true";

    if (hasValidSession() && !hasPendingReset) {
      navigate(location.state?.from || "/watchtower/dashboard", { replace: true });
    }
  }, [navigate, location.state]);

  // Prefill the UID field when "remember me" was previously selected.
  useEffect(() => {
    const savedUserId = getRememberedUserId();
    if (savedUserId) {
      form.setFieldsValue({ userId: savedUserId, remember: true });
    }
  }, [form]);

  const openAdminResetPasswordForm = useCallback(
    async (userId) => {
      setForgotOpen(true);
      setForgotSuccess(false);
      setForgotError("");
      setForgotMode("adminReset");
      setForgotStep("uid");
      setForgotLoading(true);
      forgotForm.resetFields();
      forgotForm.setFieldsValue({ uid: userId });

      try {
        const questions = await allSecurityQuestionsMutation.mutateAsync();
        setAdminSecurityQuestions(questions);
        setForgotStep("reset");
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Unable to load security questions.";
        setForgotError(msg);
      } finally {
        setForgotLoading(false);
      }
    },
    [forgotForm, allSecurityQuestionsMutation],
  );

  const handleForgotOpen = useCallback(() => {
    forgotForm.resetFields();
    setForgotSuccess(false);
    setForgotError("");
    setForgotStep("uid");
    setForgotMode("forgot");
    setPendingPostResetLogin(null);
    setForgotOpen(true);
  }, [forgotForm]);

  const handleForgotCancel = useCallback(() => {
    setForgotOpen(false);
    setPendingPostResetLogin(null);
    setForgotMode("forgot");
    setAdminSecurityQuestions([]);
  }, []);

  const handleForgotGetQuestion = useCallback(async () => {
    setForgotError("");
    setForgotLoading(true);
    try {
      const values = await forgotForm.validateFields(["uid"]);
      const result = await securityQuestionMutation.mutateAsync(values.uid);
      forgotForm.setFieldsValue({
        quesId: result.quesId,
        securityQuestion: result.question,
      });
      setForgotStep("reset");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unable to fetch security question.";
      setForgotError(msg);
    } finally {
      setForgotLoading(false);
    }
  }, [forgotForm, securityQuestionMutation]);

const handleForgotSubmit = useCallback(
  async (values) => {
    setForgotError("");
    setForgotLoading(true);

    try {
      if (!values.quesId) {
        throw new Error(
          "Security question is required. Please fetch the question first."
        );
      }

      if (forgotMode === "adminReset") {
        await changePasswordMutation.mutateAsync({
          userId: values.uid,
          questionId: Number(values.quesId),
          securityAnswer: values.securityAnswer,
          newPassword: values.newPassword,
        });

        if (pendingPostResetLogin) {
         navigate("/watchtower/dashboard");
        }

        sessionStorage.removeItem("pendingPasswordReset");
        setForgotOpen(false);
        navigate(location.state?.from || "/watchtower/dashboard");
        return;
      }

      await resetPasswordMutation.mutateAsync({
        userId: values.uid,
        quesId: Number(values.quesId),
        securityAnswer: values.securityAnswer,
        password: values.newPassword,
      });

      setForgotSuccess(true);
    } catch (err) {
      const msg =
        err?.message || "Something went wrong. Please try again.";
      setForgotError(msg);
    } finally {
      setForgotLoading(false);
    }
  },
  [
    forgotMode,
    pendingPostResetLogin,
    navigate,
    location.state,
    changePasswordMutation,
    resetPasswordMutation,
  ]
);

const handleSubmit = useCallback(
  async (values) => {
    setError(null);

    try {
      const data = await loginMutation.mutateAsync({
        userId: values.userId,
        password: values.password,
      });

      const session = buildLoginSession(data, values.userId);

      persistLoginSession(data, session);
      applyRememberMe(values.remember, values.userId);

      if (session.requiresPasswordReset) {
        sessionStorage.setItem("pendingPasswordReset", "true");
        setPendingPostResetLogin(session.loginUser);
        await openAdminResetPasswordForm(session.userId);
        return;
      }
      navigate(location.state?.from || "/watchtower/dashboard");
    } catch (e) {
      const message = e?.message || "Unable to login";
      setError(message || "Invalid User ID or password.");
    }
  },
  [loginMutation, navigate, location.state, openAdminResetPasswordForm]
);

  const handleErrorClose = useCallback(() => setError(null), []);

  return (
    <div className="login-page">
      <div className="login-page__backdrop" />

      <div className="login-page__shell">
        <LoginHero />

        <LoginForm
          form={form}
          loading={loginMutation.isPending}
          error={error}
          onSubmit={handleSubmit}
          onErrorClose={handleErrorClose}
          onForgotPasswordClick={handleForgotOpen}
        />

        <div className="login-footer">
          <Text className="login-footer__text">
            <SafetyOutlined className="login-footer__icon" />
            Your Data is Secure with Enterprise-grade encryption
          </Text>
          <Button type="link" className="login-support-button">
            Need Help? Contact Support
          </Button>
        </div>

        <Text className="login-copy">© 2026 Watch Tower PMO Suite | Version 2.1</Text>
      </div>

      <ForgotPasswordModal
        open={forgotOpen}
        loading={forgotLoading}
        success={forgotSuccess}
        mode={forgotMode}
        form={forgotForm}
        step={forgotStep}
        error={forgotError}
        securityQuestions={adminSecurityQuestions}
        onCancel={handleForgotCancel}
        onGetQuestion={handleForgotGetQuestion}
        onSubmit={handleForgotSubmit}
      />
    </div>
  );
}
